import { useMemo, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { PULSE_COLORS, SPRINT_END_DATES } from './pulseConfig.js'
import { getInitials } from './pulseHelpers.js'

const QA_TEAM_MEMBERS = ['Lana', 'Olga', 'Gavin', 'Anthony Lombardo', 'Daria', 'Lam', 'Vinh', 'Than', 'Sohil']
const US_TEAM_PREFIXES = ['Lana', 'Olga', 'Gavin', 'Daria', 'Anthony Lombardo', 'Sohil']
const EVIZI_TEAM_PREFIXES = ['Lam', 'Vinh', 'Than']

// Fixed colors per QA member — consistent across sessions
const QA_COLORS = {
    Lana: '#EC4899',
    Olga: '#8B5CF6',
    Gavin: '#10B981',
    'Anthony Lombardo': '#3B82F6',
    Daria: '#F59E0B',
    Lam: '#14B8A6',
    Vinh: '#F97316',
    Than: '#6366F1',
    Sohil: '#EF4444',
}
const FALLBACK_COLORS = ['#06B6D4', '#F97316', '#84CC16', '#6366F1']
const OTHER_KEY = 'Other (Non-QA)'
const OTHER_COLOR = '#94A3B8'

const PRIORITY_COLORS = { P0: '#EF4444', P1: '#F97316', P2: '#F59E0B', P3: '#10B981', P4: '#94A3B8' }

const TEAM_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'us', label: '🇺🇸 US Team' },
    { value: 'evizi', label: '🇻🇳 Evizi Team' },
]

const PRIORITY_PILLS = [
    { value: 'P0', color: '#EF4444' },
    { value: 'P1', color: '#F59E0B' },
    { value: 'P2', color: '#F97316' },
    { value: 'P3', color: '#10B981' },
]

function colorFor(name, index) {
    for (const [firstName, color] of Object.entries(QA_COLORS)) {
        if (name.toLowerCase().startsWith(firstName.toLowerCase())) return color
    }
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length]
}

function matchesPrefixes(name, prefixes) {
    return prefixes.some((p) => name.toLowerCase().startsWith(p.toLowerCase()))
}

function isQaMember(name) {
    return QA_TEAM_MEMBERS.some((prefix) => name.toLowerCase().startsWith(prefix.toLowerCase()))
}

function formatEndDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function CustomXTick({ x, y, payload }) {
    const sprintNum = payload?.value ? parseInt(payload.value.replace(/^S/, ''), 10) : NaN
    const isoDate = isNaN(sprintNum) ? '' : (SPRINT_END_DATES[sprintNum] ?? '')
    const dateStr = isoDate ? formatEndDate(isoDate) : ''
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={12} textAnchor="middle" fill={PULSE_COLORS.textMuted} fontSize={12}>
                {payload?.value}
            </text>
            {dateStr && (
                <text x={0} y={0} dy={26} textAnchor="middle" fill={PULSE_COLORS.textSubtle} fontSize={10}>
                    {dateStr}
                </text>
            )}
        </g>
    )
}

export default function QualityEngineerProgressChart({ sprints }) {
    const [selected, setSelected] = useState(new Set())
    const [teamFilter, setTeamFilter] = useState('all')
    const [showOther, setShowOther] = useState(true)
    const [viewMode, setViewMode] = useState('defects')
    const [priorityFilter, setPriorityFilter] = useState(new Set())

    const allMembers = useMemo(() => {
        const nameSet = new Set()
        for (const sprint of sprints) {
            for (const dev of sprint.developerStats) nameSet.add(dev.name)
        }
        return Array.from(nameSet)
    }, [sprints])

    const qaMembers = useMemo(() => {
        return QA_TEAM_MEMBERS.map(
            (prefix) => allMembers.find((n) => n.toLowerCase().startsWith(prefix.toLowerCase())) ?? prefix
        )
    }, [allMembers])

    const filteredQaMembers = useMemo(() => {
        if (teamFilter === 'us') return qaMembers.filter((n) => matchesPrefixes(n, US_TEAM_PREFIXES))
        if (teamFilter === 'evizi') return qaMembers.filter((n) => matchesPrefixes(n, EVIZI_TEAM_PREFIXES))
        return qaMembers
    }, [qaMembers, teamFilter])

    const activeMembers = selected.size > 0 ? Array.from(selected) : filteredQaMembers
    const activePriorities = priorityFilter.size > 0 ? Array.from(priorityFilter) : null

    const bugCountForStat = (stat) => {
        if (!stat) return 0
        if (activePriorities) {
            return activePriorities.reduce((sum, p) => sum + (stat.bugsOpenedByPriority?.[p] ?? 0), 0)
        }
        return stat.bugsOpened
    }

    const chartData = sprints.map((sprint) => {
        const point = { label: `S${sprint.sprintNumber}` }
        for (const memberName of activeMembers) {
            const stat = sprint.developerStats.find(
                (d) => d.name === memberName || d.name.toLowerCase().startsWith(memberName.toLowerCase())
            )
            point[memberName] = viewMode === 'defects' ? bugCountForStat(stat) : (stat?.issueCount ?? 0)
        }
        if (showOther && viewMode === 'defects') {
            point[OTHER_KEY] = sprint.developerStats
                .filter((d) => !isQaMember(d.name))
                .reduce((sum, d) => sum + bugCountForStat(d), 0)
        }
        return point
    })

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        const sorted = [...payload].sort((a, b) => b.value - a.value).filter((e) => e.value > 0)
        if (!sorted.length) return null
        const total = sorted.reduce((sum, e) => sum + e.value, 0)
        const sprintData = sprints.find((s) => `S${s.sprintNumber}` === label)
        return (
            <div
                style={{
                    backgroundColor: PULSE_COLORS.bgInner,
                    border: `1px solid ${PULSE_COLORS.border}`,
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    minWidth: 200,
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        backgroundColor: PULSE_COLORS.cardBg,
                        borderBottom: `1px solid ${PULSE_COLORS.border}`,
                        padding: '8px 12px',
                    }}
                >
                    <span style={{ fontSize: 13, fontWeight: 700, color: PULSE_COLORS.text }}>Sprint {label}</span>
                    <span
                        style={{
                            float: 'right',
                            fontSize: 11,
                            fontWeight: 600,
                            color: PULSE_COLORS.textMuted,
                            paddingTop: 1,
                        }}
                    >
                        {viewMode === 'defects' ? '🐛 Defects Opened' : '📋 QA Stories'}
                    </span>
                </div>
                <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {sorted.map((e) => (
                        <div key={e.dataKey}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: e.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span style={{ fontSize: 12, color: PULSE_COLORS.text }}>
                                        {String(e.name).split(' ')[0]}
                                    </span>
                                </div>
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: e.value > 0 ? e.color : PULSE_COLORS.textSubtle,
                                    }}
                                >
                                    {e.value}
                                </span>
                            </div>
                            {viewMode === 'defects' &&
                                e.value > 0 &&
                                (() => {
                                    const devStat = sprintData?.developerStats.find(
                                        (d) =>
                                            d.name === e.dataKey ||
                                            d.name.toLowerCase().startsWith(String(e.dataKey).toLowerCase())
                                    )
                                    const priorities = ['P0', 'P1', 'P2', 'P3', 'P4'].filter(
                                        (p) => (devStat?.bugsOpenedByPriority?.[p] ?? 0) > 0
                                    )
                                    if (!priorities.length) return null
                                    return (
                                        <div style={{ display: 'flex', gap: 6, paddingLeft: 14, marginTop: 1 }}>
                                            {priorities.map((p) => (
                                                <span
                                                    key={p}
                                                    style={{
                                                        fontSize: 10,
                                                        color: PRIORITY_COLORS[p],
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {p}:{devStat.bugsOpenedByPriority[p]}
                                                </span>
                                            ))}
                                        </div>
                                    )
                                })()}
                        </div>
                    ))}
                </div>
                <div
                    style={{
                        borderTop: `1px solid ${PULSE_COLORS.border}`,
                        padding: '7px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: PULSE_COLORS.cardBg,
                    }}
                >
                    <span style={{ fontSize: 12, fontWeight: 600, color: PULSE_COLORS.textMuted }}>Total</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: PULSE_COLORS.text }}>{total}</span>
                </div>
            </div>
        )
    }

    const toggleMember = (name) =>
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(name)) next.delete(name)
            else next.add(name)
            return next
        })

    const togglePriority = (p) =>
        setPriorityFilter((prev) => {
            const next = new Set(prev)
            if (next.has(p)) next.delete(p)
            else next.add(p)
            return next
        })

    const displayMembers = filteredQaMembers.length > 0 ? filteredQaMembers : allMembers.slice(0, 8)

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px' }}>
                        🔍 Quality Engineer Activities
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', marginTop: '2px' }}>
                        Compare stories closed and defect rates across sprints
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => setViewMode('defects')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 500,
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: viewMode === 'defects' ? '#EF4444' : '#7f1d1d',
                            color: viewMode === 'defects' ? '#fff' : '#FECDD3',
                        }}
                    >
                        <span>🐛</span>
                        <span>Defects Opened</span>
                    </button>
                    <button
                        onClick={() => setViewMode('qaStories')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 500,
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: viewMode === 'qaStories' ? '#10B981' : '#064e3b',
                            color: viewMode === 'qaStories' ? '#fff' : '#A7F3D0',
                        }}
                    >
                        <span>📋</span>
                        <span>QA Stories</span>
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {TEAM_FILTERS.map((tf) => (
                    <button
                        key={tf.value}
                        onClick={() => {
                            setTeamFilter(tf.value)
                            setSelected(new Set())
                            setShowOther(tf.value === 'all')
                        }}
                        style={{
                            padding: '4px 12px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: `1px solid ${teamFilter === tf.value ? PULSE_COLORS.primary : PULSE_COLORS.border}`,
                            backgroundColor: teamFilter === tf.value ? PULSE_COLORS.primary + '33' : 'transparent',
                            color: teamFilter === tf.value ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                            cursor: 'pointer',
                        }}
                    >
                        {tf.label}
                    </button>
                ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                <button
                    onClick={() => setSelected(new Set())}
                    style={{
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: `1px solid ${selected.size === 0 ? PULSE_COLORS.text : PULSE_COLORS.border}`,
                        backgroundColor: selected.size === 0 ? PULSE_COLORS.text : 'transparent',
                        color: selected.size === 0 ? PULSE_COLORS.bgInner : PULSE_COLORS.textMuted,
                        cursor: 'pointer',
                    }}
                >
                    All
                </button>
                {displayMembers.map((name, i) => {
                    const isActive = activeMembers.includes(name)
                    const color = colorFor(name, i)
                    return (
                        <button
                            key={name}
                            onClick={() => toggleMember(name)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                fontWeight: 500,
                                border: `1px solid ${isActive ? color : PULSE_COLORS.border}`,
                                backgroundColor: isActive ? color : 'transparent',
                                color: isActive ? '#fff' : PULSE_COLORS.textMuted,
                                cursor: 'pointer',
                            }}
                        >
                            <span
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    flexShrink: 0,
                                    backgroundColor: color,
                                }}
                            >
                                {getInitials(name)}
                            </span>
                            {name.split(' ')[0]}
                        </button>
                    )
                })}
                <button
                    onClick={() => setShowOther((prev) => !prev)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 500,
                        border: `1px solid ${showOther ? OTHER_COLOR : PULSE_COLORS.border}`,
                        backgroundColor: showOther ? OTHER_COLOR : 'transparent',
                        color: showOther ? '#fff' : PULSE_COLORS.textMuted,
                        cursor: 'pointer',
                    }}
                >
                    <span
                        style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: 700,
                            flexShrink: 0,
                            backgroundColor: OTHER_COLOR,
                        }}
                    >
                        👥
                    </span>
                    Other
                </button>
            </div>

            {viewMode === 'defects' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <button
                        onClick={() => setPriorityFilter(new Set())}
                        style={{
                            padding: '4px 12px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            fontWeight: 700,
                            border: `1px solid ${priorityFilter.size === 0 ? PULSE_COLORS.text : PULSE_COLORS.border}`,
                            backgroundColor: priorityFilter.size === 0 ? PULSE_COLORS.text : 'transparent',
                            color: priorityFilter.size === 0 ? PULSE_COLORS.bgInner : PULSE_COLORS.textMuted,
                            cursor: 'pointer',
                        }}
                    >
                        All
                    </button>
                    {PRIORITY_PILLS.map((pp) => {
                        const active = priorityFilter.has(pp.value)
                        return (
                            <button
                                key={pp.value}
                                onClick={() => togglePriority(pp.value)}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: '999px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    border: `1px solid ${active ? pp.color : PULSE_COLORS.border}`,
                                    backgroundColor: active ? pp.color + '33' : 'transparent',
                                    color: active ? pp.color : PULSE_COLORS.textMuted,
                                    cursor: 'pointer',
                                }}
                            >
                                {pp.value}
                            </button>
                        )
                    })}
                </div>
            )}

            <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                    <XAxis dataKey="label" tick={<CustomXTick />} axisLine={false} tickLine={false} height={50} />
                    <YAxis
                        tick={{ fill: PULSE_COLORS.textMuted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        label={{
                            value: viewMode === 'defects' ? 'Defects Opened' : 'QA Stories Closed',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10,
                            style: { fill: PULSE_COLORS.textMuted, fontSize: 11 },
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} wrapperStyle={{ opacity: 1, zIndex: 50 }} />
                    <Legend
                        formatter={(value) => (
                            <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>{value}</span>
                        )}
                    />
                    {activeMembers.map((memberName, i) => (
                        <Line
                            key={memberName}
                            type="monotone"
                            dataKey={memberName}
                            stroke={colorFor(memberName, i)}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    ))}
                    {showOther && viewMode === 'defects' && (
                        <Line
                            key={OTHER_KEY}
                            type="monotone"
                            dataKey={OTHER_KEY}
                            stroke={OTHER_COLOR}
                            strokeWidth={2}
                            strokeDasharray="5 3"
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}
